import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type DebugFlag = 'showIds' | 'consoleLogs' | 'videoInfo' | 'quizDebug' | 'autoFillQuiz' | 'orgContext' | 'moduleUnitIds';

export interface DebugState {
  version: number;
  showIds: boolean;
  consoleLogs: boolean;
  videoInfo: boolean;
  quizDebug: boolean;
  autoFillQuiz: boolean;
  orgContext: boolean;
  moduleUnitIds: boolean;
}

const STORAGE_KEY = 'coursebuilder-debug-flags';
const CURRENT_VERSION = 1;

const DEFAULT_DEBUG_STATE: DebugState = {
  version: CURRENT_VERSION,
  showIds: false,
  consoleLogs: false,
  videoInfo: false,
  quizDebug: false,
  autoFillQuiz: false,
  orgContext: false,
  moduleUnitIds: false,
};

const DEBUG_FLAGS: DebugFlag[] = ['showIds', 'consoleLogs', 'videoInfo', 'quizDebug', 'autoFillQuiz', 'orgContext', 'moduleUnitIds'];

@Injectable({ providedIn: 'root' })
export class DebugService {
  private state$ = new BehaviorSubject<DebugState>(this.loadState());
  private originalLog = console.log;

  constructor() {
    this.applyConsoleOverride(this.state$.value.consoleLogs);
  }

  get debugState$(): Observable<DebugState> {
    return this.state$.asObservable();
  }

  isEnabled(flag: DebugFlag): boolean {
    return this.state$.value[flag];
  }

  isAnyEnabled(): boolean {
    return DEBUG_FLAGS.some(flag => this.state$.value[flag]);
  }

  toggle(flag: DebugFlag): void {
    const current = this.state$.value;
    const updated = { ...current, [flag]: !current[flag] };
    this.setState(updated);
    if (flag === 'consoleLogs') {
      this.applyConsoleOverride(updated.consoleLogs);
    }
  }

  toggleAll(enabled: boolean): void {
    const updated = { ...this.state$.value };
    DEBUG_FLAGS.forEach(flag => updated[flag] = enabled);
    this.setState(updated);
    this.applyConsoleOverride(updated.consoleLogs);
  }

  allEnabled(): boolean {
    return DEBUG_FLAGS.every(flag => this.state$.value[flag]);
  }

  private setState(state: DebugState): void {
    this.state$.next(state);
    this.saveState(state);
  }

  private loadState(): DebugState {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { ...DEFAULT_DEBUG_STATE };
      const parsed = JSON.parse(raw);
      if (parsed.version !== CURRENT_VERSION) return { ...DEFAULT_DEBUG_STATE };
      return { ...DEFAULT_DEBUG_STATE, ...parsed };
    } catch {
      return { ...DEFAULT_DEBUG_STATE };
    }
  }

  private saveState(state: DebugState): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // localStorage full or unavailable — silently ignore
    }
  }

  private applyConsoleOverride(enabled: boolean): void {
    if (enabled) {
      console.log = this.originalLog;
    } else {
      console.log = () => {};
    }
  }
}
